import type { Licitacion } from "@/types";

export type DataQuality = "alta" | "media" | "baja";

export interface TenderBrief {
  completeness: number;
  quality: DataQuality;
  remainingDays: number | null;
  nextAction: string;
  alerts: string[];
  availableFields: number;
  totalFields: number;
}

export function buildTenderBrief(licitacion: Licitacion, now = new Date()): TenderBrief {
  const relevantFields = [
    licitacion.descripcion,
    licitacion.organismoCodigo,
    licitacion.organismoUnidad,
    licitacion.region,
    licitacion.comuna,
    licitacion.compradorNombre,
    licitacion.tipoMP,
    licitacion.montoEstimado,
    licitacion.fechaPublicacion,
    licitacion.fechaCierre,
    licitacion.fechaAperturaTecnica,
    licitacion.fechaAperturaEconomica,
    licitacion.fechaAdjudicacionEstimada,
  ];
  const availableFields = relevantFields.filter(
    (value) => value !== null && value !== undefined && value !== "",
  ).length;
  const completeness = Math.round((availableFields / relevantFields.length) * 100);
  const quality: DataQuality = completeness >= 75 ? "alta" : completeness >= 45 ? "media" : "baja";

  const closeDate = licitacion.fechaCierre ? new Date(licitacion.fechaCierre) : null;
  const remainingDays = closeDate && !Number.isNaN(closeDate.getTime())
    ? Math.ceil((closeDate.getTime() - now.getTime()) / 86_400_000)
    : null;
  const alerts: string[] = [];
  if (remainingDays !== null && remainingDays < 0) alerts.push("El plazo de recepción de ofertas ya venció.");
  else if (remainingDays !== null && remainingDays <= 3) alerts.push("El cierre es inminente: prioriza la revisión de requisitos.");
  if (licitacion.montoEstimado == null) alerts.push("Mercado Público no informa un monto estimado.");
  if (!licitacion.descripcion) alerts.push("El registro no incluye una descripción del objeto de compra.");
  if (!licitacion.compradorNombre) alerts.push("No hay un contacto comprador informado.");

  let nextAction = "Revisar los datos oficiales antes de decidir.";
  if (remainingDays !== null && remainingDays < 0) nextAction = "Verificar el resultado o la adjudicación del proceso.";
  else if (!licitacion.tracking) nextAction = "Evaluar encaje y decidir si incorporar al pipeline.";
  else if (licitacion.tracking.estadoPipeline === "postulada") nextAction = "Monitorear cambios y fecha de adjudicación.";
  else if (remainingDays !== null && remainingDays <= 3) nextAction = "Confirmar requisitos críticos antes del cierre.";
  else nextAction = "Completar análisis y asignar responsables.";

  return {
    completeness,
    quality,
    remainingDays,
    nextAction,
    alerts,
    availableFields,
    totalFields: relevantFields.length,
  };
}
